import { DropdownOption } from '@tcl-models/models';
import { PatientCohortMatchWorkflowState, SortByV3, SortByDirection } from '@tempus/stateflow-types';
import type { Activity } from '~/types/tShared';
import type { MatchStatusUpdates } from '@tempus/trials-types';
import {
  ActivityType,
  getMappedProgramSiteStatuses,
  LfsTrialStatus,
  Trial,
  TrialType,
  OperationalFlags,
  TimeProgramSiteStatus,
  getTimeProgramSiteStatusBadgeText,
  PATIENT_TRACKER_STATUSES,
  Tab,
  MatchFlag,
  GetPatientsRequestBody,
  ACTIVE_SITE_ARM_STATUSES,
  Filters,
} from '@tempus/trials-types';
import camelCase from 'lodash/camelCase';
import upperFirst from 'lodash/upperFirst';
import moment from 'moment';

import { Routes } from '~/routes';

import { SiteWithId } from '../site/types';
import {
  MATCH_STATUS_FLAGS,
  PATIENT_TRACKER_TAB,
  patientTrackerProgramTypesForRequest,
  SORT_OPTION,
  sortOptionFromDropdown,
} from './constants';
import {
  PatientTrackerFilterField,
  PatientTrackerFilters,
  PatientTrackerMultiDropdownFilters,
  PatientTrackerSingleDropdownFilters,
  PatientTrackingDetails,
  PatientTrackerStateId,
} from '../api/types/patientTrackerStore';
import { PatientCohortMatch } from '../api/types/timePatientTracking';

export const getTrialNameLabel = (trial?: Pick<Trial, 'shortName' | 'title'>) => {
  if (trial?.shortName) {
    return trial.shortName;
  }

  if (trial?.title && trial?.title?.length > 40) {
    return trial.title.slice(0, 40) + '...';
  }

  return trial?.title || '';
};

export const patientTrackerTrialFilterOptionFromId = (
  trialId: string,
  allTrials: readonly Pick<Trial, 'id' | 'shortName' | 'title'>[],
): DropdownOption => {
  const trial = allTrials.find((item) => item.id === trialId);
  return {
    label: trial ? getTrialNameLabel(trial) : trialId,
    value: String(trialId),
  };
};

export const openTrialMaterials = (trialId: string, site: SiteWithId | null) => {
  if (trialId) {
    // setting in localStorage since Redux gets cleared when opening a new tab
    localStorage.setItem('selectedUserSite', JSON.stringify(site));
    window.open(`${Routes.trialDocuments.path.replace(':trialId', trialId)}`, '_blank');
  }
};

export const getPatientTrackerRoute = (stateId?: PatientTrackerStateId) => {
  switch (stateId) {
    case PatientTrackerStateId.MATCH_REVIEW_PAGE:
      return Routes.matchReviewPage;
    case PatientTrackerStateId.TEMPUS_NURSE_PAGE:
      return Routes.tempusNursePage;
    default:
      return Routes.allPatientsPage;
  }
};

export const openPatientTrackerPage = (
  selectedUserSite: SiteWithId | null,
  patientTrackerPage: PatientTrackerStateId,
  urlParams?: string,
) => {
  const siteId = selectedUserSite?.id || '';
  localStorage.setItem('selectedUserSite', JSON.stringify(selectedUserSite));
  window.open(
    `${getPatientTrackerRoute(patientTrackerPage).path.replace(':siteId?', siteId)}?stateId=${patientTrackerPage}${
      urlParams ? `&${urlParams}` : ''
    }`,
    '_blank',
  );
};

export const trialStatusToDisplay = (status?: TimeProgramSiteStatus) => {
  if (!status) {
    return '';
  }
  if (
    ACTIVE_SITE_ARM_STATUSES.includes(status) ||
    status === TimeProgramSiteStatus.ACTIVATED_OUTSIDE_TIME_DO_NOT_MATCH
  ) {
    return 'Active at my site';
  }

  return upperFirst(getTimeProgramSiteStatusBadgeText(status).toLowerCase());
};

export const isDatePast = (date: string) => {
  const givenDate = moment(date, [moment.ISO_8601, 'YYYY-MM-DD'], true).startOf('day');
  const currentDate = moment().startOf('day');
  return givenDate.isValid() && givenDate.isBefore(currentDate);
};

const matchFlagForReq = {
  [MATCH_STATUS_FLAGS.COHORT_ON_HOLD]: MatchFlag.COHORT_ON_HOLD,
  [MATCH_STATUS_FLAGS.SITE_ON_HOLD]: MatchFlag.SITE_ON_HOLD,
  [MATCH_STATUS_FLAGS.NEW]: MatchFlag.NEW,
  [MATCH_STATUS_FLAGS.URGENT]: MatchFlag.URGENT,
  [MATCH_STATUS_FLAGS.RESCREEN]: MatchFlag.RESCREEN,
};

const tabForReq = {
  [PATIENT_TRACKER_TAB.REVIEW_REQUIRED]: Tab.REVIEW_REQUIRED,
  [PATIENT_TRACKER_TAB.WATCHLIST]: Tab.WATCHLIST,
  [PATIENT_TRACKER_TAB.CANDIDATE_NOW]: Tab.CANDIDATE_NOW,
  [PATIENT_TRACKER_TAB.SCREENING_QUEUE]: Tab.SCREENING_QUEUE,
  [PATIENT_TRACKER_TAB.PENDING_TEMPUS_REVIEW]: Tab.PENDING_TEMPUS_REVIEW,
  [PATIENT_TRACKER_TAB.ENROLLED]: Tab.ENROLLED,
  [PATIENT_TRACKER_TAB.VISITING_THIS_WEEK]: Tab.VISITING_THIS_WEEK,
  [PATIENT_TRACKER_TAB.TIME_SENSITIVE]: Tab.TIME_SENSITIVE,
  [PATIENT_TRACKER_TAB.ALL]: Tab.ALL,
};

const getSortByForReq = (option: SORT_OPTION): SortByV3[] => {
  switch (option) {
    case SORT_OPTION.URGENCY:
      return [
        { field: 'urgency_sort_ordinal', order: SortByDirection.ASC },
        {
          field: 'urgency_sort_secondary_ordinal_date',
          order: SortByDirection.ASC,
        },
      ];
    case SORT_OPTION.UPCOMING_VISIT_SOONEST_FIRST:
      return [
        {
          table: 'time_patient_visits',
          field: 'visit_date',
          order: SortByDirection.ASC,
        },
      ];
    case SORT_OPTION.UPCOMING_VISIT_FURTHEST_FIRST:
      return [
        {
          table: 'time_patient_visits',
          field: 'visit_date',
          order: SortByDirection.DESC,
        },
      ];
    case SORT_OPTION.OLDEST:
      return [{ field: 'created_at', order: SortByDirection.ASC }];
    case SORT_OPTION.NEWEST:
      return [{ field: 'created_at', order: SortByDirection.DESC }];
    default:
      return [
        { field: 'urgency_sort_ordinal', order: SortByDirection.ASC },
        {
          field: 'urgency_sort_secondary_ordinal_date',
          order: SortByDirection.ASC,
        },
      ];
  }
};

const getTimeProgramSiteStatusesForReq = (statusOpts?: DropdownOption[]): TimeProgramSiteStatus[] => {
  if (!statusOpts || statusOpts.length === 0) {
    return [];
  }

  const timeProgramSiteStatuses: TimeProgramSiteStatus[] = [];

  for (const option of statusOpts) {
    if (Object.values(TimeProgramSiteStatus).includes(option.value as TimeProgramSiteStatus)) {
      timeProgramSiteStatuses.push(option.value as TimeProgramSiteStatus);
    } else {
      const mappedStatuses = getMappedProgramSiteStatuses(option.value as LfsTrialStatus) || [];
      timeProgramSiteStatuses.push(...mappedStatuses);
    }
  }

  return timeProgramSiteStatuses;
};

/** Request filter shape for patient tracker queue APIs (extends trials-types `Filters`). */
export type PatientTrackerQueueRequestFilters = Filters & {
  programTypes?: number[];
  /** Match review multi-trial filter; omitted when a single trial uses `trialId`. */
  trialIds?: string[];
};

export const getFiltersForReq = (
  institutionId: string,
  filters: Partial<PatientTrackerFilters>,
  tab?: PATIENT_TRACKER_TAB,
  customFilterList?: PatientTrackerFilterField[],
) => {
  let sortBy: GetPatientsRequestBody['sortBy'] = [];

  let filtersForReq: PatientTrackerQueueRequestFilters = {
    ...(tab && { tabName: tabForReq[tab] }),
    institutionId,
  };

  Object.keys(filters).forEach((key) => {
    if (customFilterList && !customFilterList.includes(key as PatientTrackerFilterField)) {
      return;
    }

    const matchFlags = filters.matchStatus?.filter((opt) => Boolean(opt.subContent));
    const matchStatuses = filters.matchStatus?.filter((opt) => !opt.subContent);

    switch (key) {
      case PatientTrackerFilterField.RN_ASSIGNMENT:
        if (filters.rnAssignment) {
          filtersForReq = {
            ...filtersForReq,
            assignedNurseName: filters.rnAssignment,
          };
        }
        break;

      case PatientTrackerFilterField.TRIAL_NAME:
        filtersForReq = { ...filtersForReq, trialId: filters.trialName?.value };
        break;

      case PatientTrackerFilterField.TRIAL_NAMES: {
        const trialIds = (filters.trialNames ?? []).map((option) => String(option.value)).filter(Boolean);
        if (trialIds.length === 1) {
          filtersForReq = { ...filtersForReq, trialId: trialIds[0] };
        } else if (trialIds.length > 1) {
          filtersForReq = { ...filtersForReq, trialIds };
        }
        break;
      }

      case PatientTrackerFilterField.MATCH_STATUS:
        filtersForReq = {
          ...(matchFlags?.length
            ? {
                matchFlags: matchFlags.map((opt) => matchFlagForReq[opt.value]),
              }
            : {}),
          ...(matchStatuses?.length
            ? {
                matchStatuses: matchStatuses.map((opt) => opt.value as PatientCohortMatchWorkflowState),
              }
            : {}),
          ...filtersForReq,
        };

        break;

      case 'trialStatus':
        filtersForReq = {
          ...filtersForReq,
          trialStatuses: getTimeProgramSiteStatusesForReq(filters.trialStatus),
        };
        break;

      case 'trialType':
        filtersForReq = {
          ...filtersForReq,
          trialType: filters.trialType?.value as TrialType,
        };
        //TODO use TrialType elsewhere instead of string
        break;

      case 'physician':
        filtersForReq = { ...filtersForReq, physicianName: filters.physician };
        break;

      case 'biomarker':
        filtersForReq = { ...filtersForReq, biomarker: filters.biomarker };
        break;

      case 'patientName':
        filtersForReq = {
          ...filtersForReq,
          patientFullName: filters.patientName,
        };
        break;

      case 'patientId':
        if (filters.patientId) {
          filtersForReq = { ...filtersForReq, patientId: filters.patientId };
        }
        break;

      case 'note':
        filtersForReq = { ...filtersForReq, noteText: filters.note };
        break;

      case 'sortOrder':
        if (tab) {
          sortBy = getSortByForReq(sortOptionFromDropdown(filters.sortOrder));
        }
        break;
      default:
        break;
    }
  });

  return { sortBy, filtersForReq };
};

const dropdownOptionValue = (option: DropdownOption | null | undefined): string | null => {
  if (option == null) {
    return null;
  }
  return option.value != null ? String(option.value) : null;
};

const multiDropdownValues = (options: DropdownOption[] | undefined): string[] =>
  (options ?? [])
    .map((option) => String(option.value))
    .filter(Boolean)
    .sort();

const sortedStringArraysEqual = (left: string[], right: string[]): boolean =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const normalizeQueryStringArray = (value: unknown): string[] => {
  if (value == null || value === '') {
    return [];
  }
  if (Array.isArray(value)) {
    return value.map(String).filter(Boolean).sort();
  }
  return [String(value)].filter(Boolean);
};

/** Maps legacy match-review `trialName` query param into `trialNames` when `trialNames` is absent. */
export const applyLegacyMatchReviewTrialNameQueryParam = (
  stateMapKey: string,
  queryParams: Record<string, unknown>,
  parsedFilters: Partial<PatientTrackerFilters>,
  allTrials: readonly Pick<Trial, 'id' | 'shortName' | 'title'>[],
): void => {
  if (stateMapKey !== PatientTrackerStateId.MATCH_REVIEW_PAGE) {
    return;
  }

  const legacyTrialId = queryParams[PatientTrackerFilterField.TRIAL_NAME];
  if (legacyTrialId == null || legacyTrialId === '') {
    return;
  }

  const trialNamesInUrl = normalizeQueryStringArray(queryParams[PatientTrackerFilterField.TRIAL_NAMES]);
  if (trialNamesInUrl.length > 0) {
    return;
  }

  parsedFilters[PatientTrackerFilterField.TRIAL_NAMES] = [
    patientTrackerTrialFilterOptionFromId(String(legacyTrialId), allTrials),
  ];
  parsedFilters[PatientTrackerFilterField.TRIAL_NAME] = null;
};

/** Encodes Redux patient-tracker filters the same way {@link usePatientTrackerQueue} writes query params. */
export const patientTrackerFiltersToQueryValues = (
  filters: PatientTrackerFilters,
): Record<string, string | string[] | null> => {
  return Object.keys(filters).reduce(
    (acc, key) => {
      const field = key as PatientTrackerFilterField;
      const value = filters[field];

      if (!value) {
        acc[field] = null;
      } else if (PatientTrackerSingleDropdownFilters.includes(field)) {
        acc[field] = (value as DropdownOption).value;
      } else if (field === PatientTrackerFilterField.TRIAL_NAMES) {
        // Trial ids are opaque (UUIDs) — do not camelCase like other multiselect filters.
        const trialIds = (value as DropdownOption[]).map((item) => item.value).filter(Boolean);
        acc[field] = trialIds.length ? trialIds.map(String) : null;
      } else if (PatientTrackerMultiDropdownFilters.includes(field)) {
        acc[field] = (value as DropdownOption[]).map((item) => camelCase(String(item.value)));
      } else {
        acc[field] = value as string;
      }

      return acc;
    },
    {} as Record<string, string | string[] | null>,
  );
};

/**
 * Skips URL → Redux hydration while a Redux → URL filter write is still in flight and Redux
 * filter values are not yet reflected in the URL (see {@link usePatientTrackerQueue}).
 */
const PATIENT_TRACKER_TEXT_FILTER_FIELDS: PatientTrackerFilterField[] = [
  PatientTrackerFilterField.BIOMARKER,
  PatientTrackerFilterField.NOTE,
  PatientTrackerFilterField.PATIENT_ID,
  PatientTrackerFilterField.PATIENT_NAME,
  PatientTrackerFilterField.PHYSICIAN,
  PatientTrackerFilterField.RN_ASSIGNMENT,
];

export const shouldDeferPatientTrackerQueryFilterHydration = (
  queryParams: Record<string, unknown>,
  filters: PatientTrackerFilters,
): boolean => {
  const encoded = patientTrackerFiltersToQueryValues(filters);

  for (const field of PatientTrackerMultiDropdownFilters) {
    const expectedValues = normalizeQueryStringArray(encoded[field]);
    const actualValues = normalizeQueryStringArray(queryParams[field]);
    if (expectedValues.length > 0 && actualValues.length === 0) {
      return true;
    }
  }

  for (const field of PATIENT_TRACKER_TEXT_FILTER_FIELDS) {
    const expectedText = encoded[field] == null ? '' : String(encoded[field]);
    const actualText = queryParams[field] == null ? '' : String(queryParams[field]);
    if (expectedText !== actualText) {
      return true;
    }
  }

  return false;
};

/** True when decoded URL filter params already match Redux (avoids clobbering pending URL writes). */
export const patientTrackerQueryFilterParamsMatchFilters = (
  queryParams: Record<string, unknown>,
  filters: PatientTrackerFilters,
): boolean => {
  const expected = patientTrackerFiltersToQueryValues(filters);

  for (const field of Object.values(PatientTrackerFilterField)) {
    const expectedValue = expected[field];
    const actualValue = queryParams[field];

    if (PatientTrackerMultiDropdownFilters.includes(field)) {
      const expectedValues = normalizeQueryStringArray(expectedValue);
      const actualValues = normalizeQueryStringArray(actualValue);
      if (!sortedStringArraysEqual(expectedValues, actualValues)) {
        return false;
      }
      continue;
    }

    if (PatientTrackerSingleDropdownFilters.includes(field)) {
      const expectedScalar = expectedValue == null || expectedValue === '' ? null : String(expectedValue);
      const actualScalar = actualValue == null || actualValue === '' ? null : String(actualValue);
      if (expectedScalar !== actualScalar) {
        return false;
      }
      continue;
    }

    const expectedText = expectedValue == null ? '' : String(expectedValue);
    const actualText = actualValue == null ? '' : String(actualValue);
    if (expectedText !== actualText) {
      return false;
    }
  }

  return true;
};

/** Compares filter payloads by semantic values (dropdown values / text), not option labels. */
export const arePatientTrackerFiltersEqual = (
  current: PatientTrackerFilters,
  next: Partial<PatientTrackerFilters>,
): boolean => {
  for (const field of Object.values(PatientTrackerFilterField)) {
    const nextValue = next[field];
    if (nextValue === undefined) {
      continue;
    }

    const currentValue = current[field];

    if (PatientTrackerSingleDropdownFilters.includes(field)) {
      if (field === PatientTrackerFilterField.TRIAL_NAME) {
        if (
          dropdownOptionValue(currentValue as DropdownOption | null) !==
          dropdownOptionValue(nextValue as DropdownOption | null)
        ) {
          return false;
        }
      } else if (
        dropdownOptionValue(currentValue as DropdownOption) !== dropdownOptionValue(nextValue as DropdownOption)
      ) {
        return false;
      }
    } else if (PatientTrackerMultiDropdownFilters.includes(field)) {
      if (
        !sortedStringArraysEqual(
          multiDropdownValues(currentValue as DropdownOption[]),
          multiDropdownValues(nextValue as DropdownOption[]),
        )
      ) {
        return false;
      }
    } else if (currentValue !== nextValue) {
      return false;
    }
  }

  return true;
};

/** Match review requests site-trial rows only when that feature is enabled. Other pages stay TIME-only. */
export const withMatchReviewProgramTypes = (
  stateMapKey: string,
  filters: Partial<PatientTrackerFilters>,
  filtersForReq: Filters,
  siteTrialsEnabled: boolean,
): PatientTrackerQueueRequestFilters => {
  if (!siteTrialsEnabled || stateMapKey !== PatientTrackerStateId.MATCH_REVIEW_PAGE) {
    return filtersForReq;
  }

  return {
    ...filtersForReq,
    programTypes: patientTrackerProgramTypesForRequest(filters.programs),
  };
};

export const getMatchFlags = (ptd: PatientCohortMatch | PatientTrackingDetails) => {
  const flags = [
    ptd.urgentFlag && MATCH_STATUS_FLAGS.URGENT,
    ptd.newFlag && MATCH_STATUS_FLAGS.NEW,
    ptd.rescreenFlag && MATCH_STATUS_FLAGS.RESCREEN,
  ].filter(Boolean) as MATCH_STATUS_FLAGS[];

  if (ptd?.programSite?.pauseMatching) {
    flags.push(MATCH_STATUS_FLAGS.SITE_ON_HOLD);
  }

  if (
    [OperationalFlags.SAFETY, OperationalFlags.GENERIC, OperationalFlags.AMENDMENT].includes(
      ptd?.arm?.operationalFlag as OperationalFlags,
    ) ||
    (ptd?.arm?.operationalFlag === OperationalFlags.NON_ACTIVE_SITES &&
      !ACTIVE_SITE_ARM_STATUSES.includes(ptd.siteArm.status as TimeProgramSiteStatus))
  ) {
    flags.push(MATCH_STATUS_FLAGS.COHORT_ON_HOLD);
  }

  return flags;
};

const reversedTabForReq = Object.fromEntries(Object.entries(tabForReq).map(([key, value]) => [value, key]));

export const transformTabCounts = (input: Record<Tab, number>): Record<PATIENT_TRACKER_TAB, number> => {
  const result: Partial<Record<PATIENT_TRACKER_TAB, number>> = {};

  for (const [tab, count] of Object.entries(input)) {
    const patientTrackerTab = reversedTabForReq[tab as Tab];
    if (patientTrackerTab) {
      result[patientTrackerTab as PATIENT_TRACKER_TAB] = count;
    }
  }

  return result as Record<PATIENT_TRACKER_TAB, number>;
};

export const filterActivitiesToHide = (activities: Activity[], isInternalUser: boolean = false) => {
  return activities.filter((activity) => {
    if (activity.type === ActivityType.MatchStatusUpdates) {
      const content = activity.content as MatchStatusUpdates;

      if (
        content.newStatus === PatientCohortMatchWorkflowState.PRE_MATCH ||
        ([PatientCohortMatchWorkflowState.INTERNAL_REVIEW, PATIENT_TRACKER_STATUSES.INTERNAL_REVIEW].includes(
          content.newStatus as PatientTrackingDetails['currentState'],
        ) &&
          !isInternalUser)
      ) {
        return false;
      }
    }

    return true;
  });
};
